/**
 * reCAPTCHA v3 for the public quote funnel (bot-score Phase 1, 2026-09-13).
 *
 * Mirrors the PHP lane's deferred loader (sites/*\/assets/inc/head.php) rather
 * than inventing a second convention: the api.js script is injected LAZILY —
 * only once the contact step mounts, never on funnel entry. The funnel is
 * mobile-first and LCP-sensitive, and reCAPTCHA pulls ~hundreds of KB.
 *
 * ⚠️ FAIL-OPEN. Nothing here may block a lead. `executeFunnelRecaptcha`
 * resolves `null` on every failure (no key configured, script blocked by an
 * ad blocker or a corporate proxy, grecaptcha never ready, execute rejected,
 * or the whole thing simply took too long) and the caller submits WITHOUT a
 * token. Server-side, a missing token stamps `botScore: null` and changes
 * nothing about the mutation. See server/services/botScoreService.ts.
 */

const SITE_KEY = (process.env.REACT_APP_RECAPTCHA_SITE_KEY || "").trim();

/** The funnel's lead-create action name, as reported to reCAPTCHA. */
export const FUNNEL_LEAD_ACTION = "funnel_lead";

/** Hard cap on how long a submit may wait for a token. */
const EXECUTE_TIMEOUT_MS = 2000;
/** Hard cap on waiting for a just-injected api.js to become usable. */
const READY_TIMEOUT_MS = 1500;

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, opts: { action: string }) => Promise<string>;
    };
  }
}

let loadStarted = false;

/**
 * Inject api.js (idempotent, safe to call from any number of mounts) plus the
 * badge-hiding style. Returns immediately; readiness is awaited at execute
 * time. No-ops when no site key is configured, so an unconfigured build simply
 * never loads Google's script.
 */
export function loadFunnelRecaptcha(): void {
  try {
    if (!SITE_KEY || loadStarted) return;
    if (typeof document === "undefined") return;
    loadStarted = true;

    // Google permits hiding the badge as long as the required attribution is
    // shown in the form's fine print. Ours lives in QuotePrivacyPolicy.tsx on
    // the contact step. Same rule + same declaration the PHP sites use
    // (sites/topaesthetics.com/assets/inc/footer.php). Injected with the
    // script so the rule only ever exists on pages that load reCAPTCHA.
    const style = document.createElement("style");
    style.setAttribute("data-funnel-recaptcha", "badge");
    style.textContent = ".grecaptcha-badge{visibility:hidden !important;}";
    document.head.appendChild(style);

    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(SITE_KEY)}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  } catch {
    // A failed injection is just "no token" — never surfaced to the patient.
  }
}

/** Resolve once window.grecaptcha is usable, or false if it never shows up. */
function waitForGrecaptcha(): Promise<boolean> {
  return new Promise((resolve) => {
    if (window.grecaptcha?.execute) {
      resolve(true);
      return;
    }
    const deadline = Date.now() + READY_TIMEOUT_MS;
    const poll = window.setInterval(() => {
      if (window.grecaptcha?.execute) {
        window.clearInterval(poll);
        resolve(true);
      } else if (Date.now() >= deadline) {
        window.clearInterval(poll);
        resolve(false);
      }
    }, 50);
  });
}

/**
 * Mint a token for the lead-create submit. NEVER rejects and never waits more
 * than EXECUTE_TIMEOUT_MS in total — the lead always goes out, with or
 * without a token.
 */
export function executeFunnelRecaptcha(
  action: string = FUNNEL_LEAD_ACTION,
): Promise<string | null> {
  if (!SITE_KEY) return Promise.resolve(null);

  // Covers the case where the contact step never mounted (deep link straight
  // into a later step). Idempotent.
  loadFunnelRecaptcha();

  const token = (async (): Promise<string | null> => {
    try {
      const ready = await waitForGrecaptcha();
      if (!ready || !window.grecaptcha) return null;
      const value = await window.grecaptcha.execute(SITE_KEY, { action });
      return typeof value === "string" && value ? value : null;
    } catch {
      return null;
    }
  })();

  const cap = new Promise<null>((resolve) => {
    window.setTimeout(() => resolve(null), EXECUTE_TIMEOUT_MS);
  });

  return Promise.race([token, cap]).catch(() => null);
}
